import { useEffect, useRef, useState } from "react";
import { Play, Square } from "lucide-react";
import { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { SectionHead } from "@/components/landing/HowItWorks";
import { SHOWCASE, ANIMS, SOUNDS, stickerSrc } from "@/components/landing/data";

const STYLES = ["fire", "neon", "chrome", "ice", "toxic", "gold"];

const Sound = ({ id, label, playing, onPlay }) => (
  <button onClick={() => onPlay(id)} data-testid={`sfx-play-${id}`}
    className={`cx-fx-sound flex items-center gap-3 rounded-xl px-4 py-3 ${playing ? "is-on" : ""}`}>
    <span className="cx-fx-sound-ic w-8 h-8 rounded-full grid place-items-center shrink-0">{playing ? <Square className="w-3.5 h-3.5 text-white" /> : <Play className="w-3.5 h-3.5 text-white" />}</span>
    <span className="text-sm text-white text-left whitespace-nowrap truncate">{label}</span>
    <span className="ml-auto flex items-end gap-[3px] h-4">{[0, 1, 2, 3].map((b) => <span key={b} className={`w-[3px] rounded bg-gradient-to-t from-[#a746ff] to-[#3ad7ff] ${playing ? "cx-eq" : "h-1 opacity-40"}`} style={{ animationDelay: `${b * 0.12}s` }} />)}</span>
  </button>
);

const useSound = () => {
  const ref = useRef(null);
  const [cur, setCur] = useState(null);
  const play = (id) => {
    ref.current?.pause();
    if (cur === id) { setCur(null); return; }
    const a = new Audio(`${API}/fx/sfx/${id}.mp3`);
    a.volume = 0.8; a.onended = () => setCur(null); a.play().catch(() => setCur(null));
    ref.current = a; setCur(id);
  };
  useEffect(() => () => ref.current?.pause(), []);
  return [cur, play];
};

export const FxShowcase = () => {
  const { t } = useI18n();
  const [anim, setAnim] = useState("slam");
  const [pick, setPick] = useState("headshot");
  const [text, setText] = useState("CLIP-EX");
  const [style, setStyle] = useState("fire");
  const [cur, play] = useSound();
  const [k, setK] = useState(0);
  const choose = (fn) => { fn(); setK((x) => x + 1); };
  const custom = `${API}/fx/sticker/custom.png?text=${encodeURIComponent((text.trim() || "GG").toUpperCase())}&style=${style}`;
  return (
    <section id="fx" className="relative py-24 scroll-mt-20" data-testid="fx-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead label={t("ld.fx_label")} title={t("ld.fx_title")} sub={t("ld.fx_sub")} />
        <div className="grid lg:grid-cols-[1fr_380px] gap-8 items-start">
          <div className="cx-fx-panel rounded-2xl p-5 sm:p-7">
            <div className="flex flex-wrap gap-2 mb-6" data-testid="sticker-anim-select">
              {ANIMS.map((a) => <button key={a} onClick={() => choose(() => setAnim(a))} data-testid={`ld-anim-${a}`}
                className={`cx-fx-pill rounded-full px-3.5 py-1.5 text-xs font-mono ${anim === a ? "is-on" : ""}`}>{t(`st.anim_${a}`)}</button>)}
            </div>
            <div className="grid grid-cols-[1fr] sm:grid-cols-[1fr_1.2fr] gap-6 items-center">
              <div className="cx-fx-stage relative aspect-square rounded-2xl grid place-items-center overflow-hidden cx-grid-bg" data-testid="ld-sticker-stage">
                <span className="cx-fx-halo" />
                <span className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em] text-[#e9c6ff] bg-black/50 border border-[#a746ff]/40" data-testid="ld-sticker-name">{pick}</span>
                <div key={`${pick}-${anim}-${k}`} className="relative z-[1] w-[78%] cx-st" style={{ "--d": "60s" }}><div className={`cx-st-${anim}`}><img src={stickerSrc(pick)} alt="" className="w-full" /></div></div>
              </div>
              <div className="grid grid-cols-6 gap-2" data-testid="sticker-grid">
                {SHOWCASE.map((id) => <button key={id} onClick={() => choose(() => setPick(id))} data-testid={`sticker-item-${id}`}
                  className={`cx-fx-tile aspect-square rounded-xl p-1 ${pick === id ? "is-on" : ""}`}><img src={stickerSrc(id)} alt={id} loading="lazy" className="w-full h-full object-contain" /></button>)}
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <div className="cx-fx-panel rounded-2xl p-5">
              <div className="cx-label mb-3">{t("ld.fx_custom")}</div>
              <input value={text} maxLength={24} onChange={(e) => setText(e.target.value)} data-testid="custom-sticker-input"
                className="w-full rounded-lg bg-[#070912] border border-white/10 px-4 py-2.5 text-sm text-white uppercase focus:outline-none focus:border-[#3ad7ff]/60" />
              <div className="flex flex-wrap gap-1.5 mt-3">{STYLES.map((s) => <button key={s} onClick={() => setStyle(s)} data-testid={`ld-style-${s}`}
                className={`cx-fx-pill rounded-full px-3.5 py-1.5 text-xs ${style === s ? "is-on" : ""}`}>{t(`st.style_${s}`)}</button>)}</div>
              <div className="cx-fx-stage relative h-32 mt-4 rounded-xl grid place-items-center overflow-hidden"><span className="cx-fx-halo" /><img src={custom} alt="" className="relative z-[1] max-h-full max-w-full object-contain cx-pop" key={custom} data-testid="ld-custom-preview" /></div>
            </div>
            <div>
              <div className="cx-label mb-3">{t("ld.fx_sounds")}</div>
              <div className="grid grid-cols-2 gap-2" data-testid="sfx-grid">{SOUNDS.map(([id, l]) => <Sound key={id} id={id} label={l} playing={cur === id} onPlay={play} />)}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
